import React from 'react';
import styled, { css } from 'styled-components/macro';

import List from '../styled/DefinitionList/DefinitionList';
import KeyValuePair from '../base/KeyValuePair/KeyValuePair';
import modularScale from '../../styles/helpers/modular-scale';

const definitionListGridStyle = ({ theme }: any): any => css<any>`
  flex-flow: column nowrap;
  display: grid;

  ${({ isRedesigned }): any =>
    !isRedesigned &&
    css`
      grid-template-columns: repeat(2, 1fr);
    `};

  padding: ${`${theme.spacing.medium}`};
  width: 100%;

  > div {
    display: flex;
    flex-flow: column nowrap;
    justify-content: space-between;
    margin: ${`${theme.spacing.medium} 0`};

    &:first-of-type {
      margin-left: 0;
    }

    &:last-of-type {
      margin-right: 0;
    }
  }

  @media screen and (min-width: 24.875em) {
    flex-flow: row wrap;

    > div {
      margin: ${`${theme.spacing.medium}`};
    }
  }

  @supports (grid-gap: ${`${theme.spacing.medium}`}) {
    grid-gap: ${`${theme.spacing.medium}`};

    > div {
      margin: 0;
    }
  }

  @media screen and (min-width: 40em) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (min-width: 60em) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media screen and (min-width: 90em) {
    grid-template-columns: repeat(6, 1fr);
  }

  ${({ rows }): any =>
    rows &&
    css`
      @media screen and (min-width: 50em) {
        grid-template-columns: repeat(${rows}, 1fr);
      }
      @media screen and (max-width: 50em) {
        grid-template-columns: repeat(1, 1fr);
      }
    `};

  @media screen and (min-width: 120em) {
    padding: ${`${theme.spacing.large} ${theme.spacing.medium}`};
  }
`;

const DefinitionList = styled(List)<any>`
  ${definitionListGridStyle};

  & + & {
    width: auto;
  }

  dd {
    font-size: ${modularScale(1)};

    @media screen and (min-width: 40em) {
      font-size: ${modularScale(3)};
    }
  }
`;

export const ComponentStatisticsWrapper = styled('div')`
  display: flex;
  flex-wrap: wrap;
  margin: ${({ theme }): string => `0 -${theme.spacing.medium} 0`};
  overflow: hidden;

  dl > div {
    width: max-content;
  }

  &:first-child {
    ${DefinitionList} {
      padding-top: 0;
    }
  }
`;

export const ComponentStatisticsLoaderWrapper = styled('div')<any>`
  ${definitionListGridStyle};
  padding-left: 0;
  padding-right: 0;
`;

interface IComponentStatisticsProps {
  statistics: any[];
  withTooltip?: boolean;
  isRedesigned?: boolean;
  isMobileView?: boolean;
  rows?: number;
  keyValueType?: string;
  mobileRow?: boolean;
}

/**
 * ComponentStatistics returns one or multiple DefinitionList components.
 */
const ComponentStatistics: React.FC<IComponentStatisticsProps> = ({
  statistics,
  withTooltip = false,
  isRedesigned = false,
  isMobileView = false,
  rows,
  keyValueType,
  mobileRow,
}) => {
  if (!statistics) return null;

  const hasArray = statistics.reduce((acc, el) => {
    return acc || Array.isArray(el);
  }, false);

  // If statistics contains Arrays, we need multiple DefinitionList components.
  if (hasArray) {
    return (
      <ComponentStatisticsWrapper>
        {statistics.map((el, idx) => {
          if (Array.isArray(el)) {
            return (
              <DefinitionList
                isRedesigned={isRedesigned}
                rows={rows}
                key={`ComponentStatistics-${idx}`}
                data-pw="statistics-group"
              >
                {el.map((stat, index) => (
                  <KeyValuePair
                    key={`ComponentStatistics-${idx}-${index}`}
                    withTooltip={withTooltip}
                    isMobileView={isMobileView}
                    keyValueType={keyValueType}
                    mobileRow={mobileRow}
                    {...stat}
                  />
                ))}
              </DefinitionList>
            );
          }

          return (
            <DefinitionList
              isRedesigned={isRedesigned}
              rows={rows}
              key={`ComponentStatistics-${idx}`}
              data-pw="statistics-group"
            >
              <KeyValuePair
                {...el}
                isMobileView={isMobileView}
                withTooltip={withTooltip}
                keyValueType={keyValueType}
                mobileRow={mobileRow}
              />
            </DefinitionList>
          );
        })}
      </ComponentStatisticsWrapper>
    );
  }

  return (
    <ComponentStatisticsWrapper>
      <DefinitionList
        data-pw="statistics-group"
        isRedesigned={isRedesigned}
        rows={rows}
      >
        {statistics.map((stat, index) => (
          <KeyValuePair
            key={`csw-dl-statistics-${index}`}
            {...stat}
            withTooltip={withTooltip}
            isMobileView={isMobileView}
            keyValueType={keyValueType}
            mobileRow={mobileRow}
          />
        ))}
      </DefinitionList>
    </ComponentStatisticsWrapper>
  );
};

export default ComponentStatistics;
